import { z } from 'zod';

import {
  entityIdSchema,
  nonEmptyTextSchema,
  organizationSnapshotSchema,
  sha256Schema,
  utcDateTimeSchema,
} from './primitives';
import { resourceScopeSchema } from './tenant-rbac';

export const auditActorSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('user'),
      identityId: entityIdSchema,
      displayName: nonEmptyTextSchema.max(100),
      organization: organizationSnapshotSchema,
      sessionId: entityIdSchema.nullable(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('system'),
      component: nonEmptyTextSchema.max(100),
    })
    .strict(),
  z
    .object({
      kind: z.literal('service'),
      serviceName: nonEmptyTextSchema.max(100),
    })
    .strict(),
]);

export const auditChangeSchema = z
  .object({
    path: z.string().trim().min(1).max(500),
    operation: z.enum(['add', 'replace', 'remove']),
    beforeHash: sha256Schema.nullable(),
    afterHash: sha256Schema.nullable(),
  })
  .strict()
  .superRefine((change, context) => {
    if (change.operation === 'add' && change.afterHash === null) {
      context.addIssue({ code: 'custom', path: ['afterHash'], message: 'Add requires afterHash' });
    }
    if (change.operation === 'remove' && change.beforeHash === null) {
      context.addIssue({
        code: 'custom',
        path: ['beforeHash'],
        message: 'Remove requires beforeHash',
      });
    }
    if (
      change.operation === 'replace' &&
      (change.beforeHash === null || change.afterHash === null)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['afterHash'],
        message: 'Replace requires beforeHash and afterHash',
      });
    }
  });

export const auditEventSchema = z
  .object({
    schemaVersion: z.literal(1),
    eventId: entityIdSchema,
    occurredAt: utcDateTimeSchema,
    recordedAt: utcDateTimeSchema,
    scope: resourceScopeSchema,
    actor: auditActorSchema,
    action: z
      .string()
      .trim()
      .min(3)
      .max(150)
      .regex(/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/, 'Use a namespaced audit action'),
    resource: z
      .object({
        resourceType: nonEmptyTextSchema.max(100),
        resourceId: entityIdSchema,
        resourceVersionId: entityIdSchema.nullable(),
      })
      .strict(),
    outcome: z.enum(['success', 'denied', 'failure']),
    reason: z.string().trim().max(2_000).nullable(),
    correlationId: entityIdSchema,
    causationId: entityIdSchema.nullable(),
    requestId: entityIdSchema.nullable(),
    changes: z.array(auditChangeSchema),
  })
  .strict()
  .superRefine((event, context) => {
    if (Date.parse(event.recordedAt) < Date.parse(event.occurredAt)) {
      context.addIssue({
        code: 'custom',
        path: ['recordedAt'],
        message: 'recordedAt cannot precede occurredAt',
      });
    }
    if (event.outcome !== 'success' && !event.reason) {
      context.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'Denied and failed actions require a reason',
      });
    }
  });

export type AuditActor = z.infer<typeof auditActorSchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;
